import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { PropTypes } from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import { useNavigate } from 'react-router-dom';
import { EntryType } from '../../API';
import RecipeCollectionsAddMealCard from './recipes_collections_add_meal_card';
import { END_USER_RECIPE_SINGLE_COLLECTION_ROUTE } from '../../services/routes';
import { recipeCollectionsCardDOMID } from '../../services/meals';
import {
  useCreateMealMutation,
  useGetGRCRecipesByIDsQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
  useUpdateSmorgBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  copyMealToBoard,
  copySharedMealToBoard,
} from '../../operations/recipes_operations';
import { useTracker } from '../../operations/tracking_operations';

const RecipeCollectionsSearchResultItem = ({
  entryType,
  objectID,
  parentID,
  title,
  destinationBoardID,
  destinationMenuID,
  onClick,
}) => {
  const dispatch = useDispatch();
  const tracker = useTracker();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const toRecipesBoard = (ownedRecipesBoards || []).find(
    (board) => board.id === destinationBoardID,
  );

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const navigate = useNavigate();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [createMeal] = useCreateMealMutation();

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery(
    { id: objectID, parentID },
    { skip: !objectID || !parentID },
  );

  const { data: distilledMealInSharedMeals } =
    useGetDistilledSharedMealByIDAndParentQuery(
      { id: objectID, parentID },
      {
        skip: !objectID || !parentID,
      },
    );

  const isSharedMeal = !!distilledMealInSharedMeals;

  const { data: grcRecipes } = useGetGRCRecipesByIDsQuery(
    [objectID],
    /* GRC recipes don't have a parent */ { skip: !objectID || !!parentID },
  );

  const grcRecipe = (grcRecipes || {})[objectID];

  const distilledRecipeObj =
    entryType === EntryType.MEAL
      ? (distilledMealInMeals?.recipes ||
          distilledMealInSharedMeals?.recipes ||
          [])[0]
      : grcRecipe?.recipe;

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const onAdd = useCallback(async () => {
    // TODO handle GRC results?
    const cb = (copiedMeal) => {
      window.setTimeout(() => {
        const el = document.getElementById(
          recipeCollectionsCardDOMID(copiedMeal.id),
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    };

    if (isSharedMeal) {
      const { data: fullSharedMeal } = await lazyGetSharedMeal(objectID);
      await copySharedMealToBoard(
        fullSharedMeal,
        toRecipesBoard,
        destinationMenuID,
        currentSpaceMembership,
        updateSmorgBoard,
        createMeal,
        userProfile,
        tracker,
        null,
        cb,
        dispatch,
      );
    } else {
      const { data: fullMeal } = await lazyGetMeal(objectID);
      await copyMealToBoard(
        fullMeal,
        toRecipesBoard,
        destinationMenuID,
        currentSpaceMembership,
        updateSmorgBoard,
        createMeal,
        userProfile,
        tracker,
        null,
        cb,
        dispatch,
        null,
      );
    }
    navigate(
      formatRoute(END_USER_RECIPE_SINGLE_COLLECTION_ROUTE, {
        boardID: destinationBoardID,
      }),
    );
  }, [
    isSharedMeal,
    navigate,
    destinationBoardID,
    lazyGetSharedMeal,
    objectID,
    toRecipesBoard,
    destinationMenuID,
    currentSpaceMembership,
    updateSmorgBoard,
    createMeal,
    userProfile,
    tracker,
    dispatch,
    lazyGetMeal,
  ]);

  return (
    <RecipeCollectionsAddMealCard
      title={title}
      imageUrl={distilledRecipeObj?.imageUrl}
      onAdd={onAdd}
      onClick={() => onClick(objectID)}
      addEnabled={!!toRecipesBoard}
    />
  );
};

RecipeCollectionsSearchResultItem.propTypes = {
  entryType: PropTypes.string.isRequired,
  objectID: PropTypes.string.isRequired,
  parentID: PropTypes.string,
  title: PropTypes.string.isRequired,
  destinationBoardID: PropTypes.string.isRequired,
  destinationMenuID: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

RecipeCollectionsSearchResultItem.defaultProps = {
  parentID: null,
};

export default RecipeCollectionsSearchResultItem;
